import { apiFetch } from '@/stores/authStore';
import type { FrozenCancelSnapshot } from './gameOrderSelection';

export const CANCEL_ALL_AUDIT_TIMEOUT_MS = 750;

/**
 * The relay REFUSED a Spacebar pull before any venue write — the LT3_PULL_ALL
 * kill switch, a revoked LT2 grant, an unwritable attempt row (503), or an
 * old relay whose standalone authorizer refused the fallback. Thrown by
 * `accountStore.cancelOrdersByTickers` on the `live_pull_all` surface so the
 * pull can report `refused` (not "N FAILED"): nothing was sent, nothing needs
 * a retry. `code` mirrors the relay's `code` field when it sent one.
 */
export class PullAllRefusedError extends Error {
  readonly code: string | null;
  constructor(message: string, code: string | null = null) {
    super(message);
    this.name = 'PullAllRefusedError';
    this.code = code;
  }
}

/**
 * The scopes this breadcrumb can describe. Position Tracker is the only surface
 * that records one (see `surface` below), and it sweeps by filter, never by
 * ladder level — so the parameter EXCLUDES #523's `'level'` rather than letting
 * it fall through to `'all'`. A single price level logged as an account-wide
 * sweep is a falsified audit record; keeping it out of the type means a future
 * caller fails to compile instead of writing one.
 */
export type AuditableCancelSnapshot = FrozenCancelSnapshot & {
  scope: 'visible' | 'all' | 'game';
};

/** The V3 Spacebar pull's breadcrumb body: truthful surface + game scope.
 *  The relay accepts live_pull_all ONLY with scope game — recording a pull
 *  as an account-wide sweep would falsify the audit the same way a #523
 *  level mislabel would. */
export function buildPullAllAttemptBody(
  snapshot: FrozenCancelSnapshot & { scope: 'game' },
  attemptId: string = crypto.randomUUID(),
) {
  return {
    attemptId,
    snapshotId: snapshot.id,
    surface: 'live_pull_all' as const,
    scope: 'game' as const,
    targets: snapshot.targets.map(({ venue, orderId }) => ({ venue, orderId })),
  };
}

/**
 * The V3 Spacebar pull's server AUTHORIZATION — deliberately NOT the
 * degrade-not-block breadcrumb contract recordBulkCancelAttempt keeps below.
 * The relay refuses this request while the LT3_PULL_ALL kill switch is off
 * or the trader's LT2 grant is revoked, and the caller treats ANY failure —
 * refusal, network error, or the short timeout — as a refusal of the whole
 * pull before a single cancel is sent. Fail closed: an unreachable
 * authorizer means no sweep, never a sweep with a missing record.
 */
export async function recordPullAllAttempt(
  snapshot: FrozenCancelSnapshot,
  /** Correlation id shared with the merged cancel-sequence call (speed-demon
   *  D2): the old-relay FALLBACK authorizer records the same attemptId the
   *  pull would otherwise have sent in one request. */
  attemptId?: string,
): Promise<void> {
  if (snapshot.scope !== 'game') {
    throw new Error('A Pull All breadcrumb must describe a game-scoped snapshot');
  }
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const timeoutFailure = new Promise<never>((_resolve, reject) => {
    timeout = setTimeout(() => {
      controller.abort();
      reject(new Error('Pull All attempt log timed out'));
    }, CANCEL_ALL_AUDIT_TIMEOUT_MS);
  });
  try {
    const res = await Promise.race([
      apiFetch('/api/kalshi/orders/cancel-all-attempt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          buildPullAllAttemptBody(snapshot as FrozenCancelSnapshot & { scope: 'game' }, attemptId),
        ),
        signal: controller.signal,
      }),
      timeoutFailure,
    ]);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(
        (body as Record<string, string>).error ?? `Pull All attempt log failed (${res.status})`,
      );
    }
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}

export function buildBulkCancelAttemptBody(
  snapshot: AuditableCancelSnapshot,
  attemptId: string = crypto.randomUUID(),
) {
  return {
    attemptId,
    snapshotId: snapshot.id,
    surface: 'position_tracker' as const,
    scope: snapshot.scope === 'visible' ? ('visible' as const) : ('all' as const),
    targets: snapshot.targets.map(({ venue, orderId }) => ({ venue, orderId })),
  };
}

/**
 * Best-effort evidence only. A short abort keeps audit degradation from
 * becoming a cancellation delay; callers surface the error and continue with
 * the frozen per-order requests.
 */
export async function recordBulkCancelAttempt(snapshot: FrozenCancelSnapshot): Promise<void> {
  // Fail closed rather than mislabel. This also narrows `snapshot` for the
  // builder below, so the guarantee is enforced at runtime AND checked at the
  // type level without narrowing this function's own parameter — it is passed
  // as `BulkCancelDeps.recordAttempt`, whose signature takes every scope.
  // The executor reports a thrown recordAttempt as `auditError` and still sends
  // the venue cancels, so this degrades the breadcrumb, never the cancellation.
  if (snapshot.scope === 'level') {
    throw new Error('A price-level cancel must never be recorded as an account-wide sweep');
  }
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const timeoutFailure = new Promise<never>((_resolve, reject) => {
    timeout = setTimeout(() => {
      controller.abort();
      reject(new Error('Cancel All attempt log timed out'));
    }, CANCEL_ALL_AUDIT_TIMEOUT_MS);
  });
  try {
    const res = await Promise.race([
      apiFetch('/api/kalshi/orders/cancel-all-attempt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The guard above proves the scope; TS narrows the property, not the object.
        body: JSON.stringify(buildBulkCancelAttemptBody(snapshot as AuditableCancelSnapshot)),
        signal: controller.signal,
      }),
      timeoutFailure,
    ]);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(
        (body as Record<string, string>).error ?? `Cancel All attempt log failed (${res.status})`,
      );
    }
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}
